import React, { useState, useEffect } from "react";
import { getFirestore, collection, query, where, onSnapshot } from "firebase/firestore";
import { auth } from "./firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

const db = getFirestore();

// 🗓 Helpers
function startOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getMonthDays(baseDate = new Date()) {
  const start = startOfMonth(baseDate);
  const month = start.getMonth();
  const days = [];
  while (start.getMonth() === month) {
    days.push(new Date(start));
    start.setDate(start.getDate() + 1);
  }
  return days;
}

export default function CalendarView() {
  const [reminders, setReminders] = useState([]);
  const [baseDate, setBaseDate] = useState(new Date());

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      if (!u) {
        setReminders([]);
        return;
      }

      const q = query(collection(db, "reminders"), where("userId", "==", u.uid));
      const unsubscribeReminders = onSnapshot(q, (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setReminders(data);
      });

      return () => unsubscribeReminders();
    });

    return () => unsubscribeAuth();
  }, []);

  const days = getMonthDays(baseDate);
  const currentMonth = baseDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const today = new Date();

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f4f3ee",
        padding: "40px 20px",
      }}
    >
      <h2 style={{ textAlign: "center", marginBottom: "20px", color: "#4c6042" }}>
        {currentMonth}
      </h2>

      {/* Navigation */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginBottom: "25px",
        }}
      >
        <button
          onClick={() =>
            setBaseDate(
              new Date(baseDate.getFullYear(), baseDate.getMonth() - 1, 1)
            )
          }
          style={navBtnStyle}
        >
          ◀ Previous Month
        </button>
        <button onClick={() => setBaseDate(new Date())} style={navBtnStyleLight}>
          Current Month
        </button>
        <button
          onClick={() =>
            setBaseDate(
              new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 1)
            )
          }
          style={navBtnStyle}
        >
          Next Month ▶
        </button>
      </div>

      {/* Calendar grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "20px",
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        {days.map((d) => {
          const dayReminders = reminders.filter((r) => {
            const dateVal = r.date?.toDate ? r.date.toDate() : null;
            return dateVal && dateVal.toDateString() === d.toDateString();
          });

          const isToday = d.toDateString() === today.toDateString();
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;

          return (
            <div
              key={d.toDateString()}
              style={{
                backgroundColor: isToday ? "#eaf0ea" : "#ffffff",
                borderRadius: "12px",
                padding: "14px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                display: "flex",
                flexDirection: "column",
                minHeight: "140px",
                borderLeft: `6px solid ${
                  isWeekend ? "#b7a99a" : "#91a58b"
                }`,
                opacity: d.getMonth() === baseDate.getMonth() ? 1 : 0.4,
              }}
            >
              <div style={{ fontWeight: "bold", color: "#4c6042" }}>
                {d.toLocaleDateString(undefined, { weekday: "short" })}{" "}
                {d.getDate()}
              </div>

              {dayReminders.length > 0 ? (
                <ul style={{ listStyle: "none", padding: 0, marginTop: "8px" }}>
                  {dayReminders.map((r) => (
                    <li
                      key={r.id}
                      style={{
                        backgroundColor: "#f8f9f7",
                        marginBottom: "5px",
                        padding: "6px 8px",
                        borderRadius: "6px",
                        fontSize: "13px",
                        color: "#333",
                      }}
                    >
                      {r.text}
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ fontSize: "12px", color: "#aaa", marginTop: "8px" }}>
                  No reminders
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const navBtnStyle = {
  padding: "8px 14px",
  borderRadius: "8px",
  border: "none",
  backgroundColor: "#91a58b",
  color: "white",
  cursor: "pointer",
};

const navBtnStyleLight = {
  ...navBtnStyle,
  backgroundColor: "#d6cbbf",
  color: "#333",
};
